import { useEffect, useRef } from "react";
import axios from "axios";
import { CardViewProps } from "../components/home/NewsCardView";
import FooterView from "../components/home/layout/FooterView";
import NewsCardListView from "../components/home/NewsCardListView";
import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import useCountUp from "../hooks/home/useCountUp";
import FadeInOnScroll from "../components/common/FadeInOnScroll";
import { Variants } from "framer-motion";

export type CardVariant = "hero" | "teaser" | "normal" | "multi" | "action";

export interface MainpageProps {
  entity: NewsCardLetterViewProps[];
}

export interface NewsCardLetterViewProps {
  variant: CardVariant;
  imageUrl?: string;
  category?: string;
  index: number;
  title: string;
  description?: string;
  pointColor?: string;
  textColor?: string;
  meta?: string;
  cta?: string;
  callBack?: () => void;
}

function HardCard({
  card,
  index,
  useNavigate,
}: {
  card: NewsCardLetterViewProps;
  index: number;
  useNavigate: () => void;
}) {
  const onClick = useNavigate ?? (() => {});
  return (
    <a onClick={onClick} className={`bento-item item-${index} group relative`}>
      <div className="relative h-full laptop:h-full">
        <img
          src={card.imageUrl}
          alt={card.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      </div>
      <div className="bento-item-content absolute left-0 right-0 bottom-0 text-white p-6">
        <h2 className="text-2xl laptop:text-3xl font-bold underline whitespace-pre-line">
          {card.title}
        </h2>
      </div>
    </a>
  );
}

function TeaserCard({
  card,
  index,
  useNavigate,
}: {
  card: NewsCardLetterViewProps;
  index: number;
  useNavigate: () => void;
}) {
  const onClick = useNavigate ?? (() => {});

  return (
    <a onClick={onClick} className={`bento-item item-${index} group`}>
      <div className="bento-item-content relative p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-8 w-1 bg-blue-600"></div>
          <span className="text-blue-600 font-medium text-sm uppercase tracking-wide">
            {card.category}
          </span>
        </div>
        <h3 className="text-lg font-bold flex-grow group-hover:underline">
          {card.title}
        </h3>
        <p className="text-sm text-gray-500 mt-2">{card.meta}</p>
      </div>
    </a>
  );
}

function NormalCard({
  card,
  index,
  useNavigate,
}: {
  card: NewsCardLetterViewProps;
  index: number;
  useNavigate: () => void;
}) {
  const onClick = useNavigate ?? (() => {});

  return (
    <a onClick={onClick} className={`bento-item item-${index} group`}>
      <div className="relative">
        <img src={card.imageUrl} className="w-full h-32 object-cover" />
        <div className="bento-item-content">
          <div className="absolute top-4 left-4">
            <div
              style={{ backgroundColor: card.pointColor }}
              className="text-white px-3 py-1 rounded-md"
            >
              <span className="font-medium text-xs uppercase tracking-wide">
                {card.category}
              </span>
            </div>
          </div>
          <h3 className="text-base font-bold flex-grow group-hover:underline">
            {card.title}
          </h3>
        </div>
      </div>
    </a>
  );
}

function MultiCard({
  card,
  index,
  useNavigate,
}: {
  card: NewsCardLetterViewProps;
  index: number;
  useNavigate: () => void;
}) {
  const onClick = useNavigate ?? (() => {});

  return (
    <a onClick={onClick} className={`bento-item item-${index} group`}>
      <div className="bento-item-content">
        <div className="flex items-center gap-3 mb-5">
          <div
            className="h-8 w-1"
            style={{ backgroundColor: card.pointColor }}
          ></div>
          <span
            className={`text-[#b81414] font-medium text-sm uppercase tracking-wide`}
          >
            {card.category}
          </span>
        </div>
        <p className="text-lg text-gray-900 mb-3">{card.title}</p>
        <p className="text-sm text-gray-600 leading-relaxed">
          {card.description}
        </p>
      </div>
    </a>
  );
}

function ActionCard({
  card,
  index,
  useNavigate,
}: {
  card: NewsCardLetterViewProps;
  index: number;
  useNavigate: () => void;
}) {
  const onClick = useNavigate ?? (() => {});

  return (
    <a className={`bento-item item-${index} group bg-[#132551] text-white`}>
      <div className="bento-item-content items-center justify-center text-center">
        <h3 className="text-xl font-bold mb-2">{card.title}</h3>
        <p className="text-sm font-bold mb-4">{card.description}</p>
        <button
          onClick={onClick}
          className="bg-white text-[#0A2463] font-bold py-2 px-6 rounded-full hover:bg-gray-200 transition-colors"
        >
          {card.cta}
        </button>
      </div>
    </a>
  );
}

const MainPage = ({ entity }: MainpageProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [receiptCounter, setReceiptCounter] = useState<number>(0);

  useEffect(() => {
    const fetchReceiptCount = async () => {
      try {
        const response = await axios.get<{ currentCounter: number }>(
          "/getCounter"
        );
        const data = response.data;
        setReceiptCounter(data.currentCounter);
      } catch (error) {
        console.error("Error fetching receipt count:", error);
      }
    };

    fetchReceiptCount();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    const mouse = {
      x: undefined as number | undefined,
      y: undefined as number | undefined,
      radius: 150,
    };
    class Particle {
      x: number;
      y: number;
      directionX: number;
      directionY: number;
      size: number;
      color: string;
      constructor(
        x: number,
        y: number,
        dx: number,
        dy: number,
        size: number,
        color: string
      ) {
        this.x = x;
        this.y = y;
        this.directionX = dx;
        this.directionY = dy;
        this.size = size;
        this.color = color;
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
      }
      update() {
        if (this.x > canvas.width || this.x < 0)
          this.directionX = -this.directionX;
        if (this.y > canvas.height || this.y < 0)
          this.directionY = -this.directionY;
        if (mouse.x !== undefined && mouse.y !== undefined) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const dist = Math.hypot(dx, dy);
          if (dist < mouse.radius + this.size) {
            if (mouse.x < this.x && this.x < canvas.width - this.size * 10)
              this.x += 5;
            if (mouse.x > this.x && this.x > this.size * 10) this.x -= 5;
            if (mouse.y < this.y && this.y < canvas.height - this.size * 10)
              this.y += 5;
            if (mouse.y > this.y && this.y > this.size * 10) this.y -= 5;
          }
        }
        this.x += this.directionX;
        this.y += this.directionY;
        this.draw();
      }
    }

    function setSize() {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      mouse.radius = (canvas.height / 80) * (canvas.height / 80);
    }
    setSize();

    window.addEventListener("mousemove", onMouseover);
    window.addEventListener("mouseout", onMouseOut);
    window.addEventListener("resize", onResize);
    let particlesArray: Particle[] = [];
    init();
    animate();

    const scenes = document.querySelectorAll<HTMLElement>(".scene");
    const observer = new IntersectionObserver(onIntersect, { threshold: 0.5 });
    scenes.forEach((el) => observer.observe(el));

    function onMouseover(e: MouseEvent) {
      mouse.x = e.x;
      mouse.y = e.y;
    }

    function onMouseOut() {
      mouse.x = undefined;
      mouse.y = undefined;
    }

    function onResize() {
      setSize();
      init();
    }

    function init() {
      particlesArray = [];
      const num = (canvas.width * canvas.height) / 9000;
      for (let i = 0; i < num; i++) {
        const size = Math.random() * 2 + 1;
        const x = Math.random() * (canvas.width - size * 4) + size * 2;
        const y = Math.random() * (canvas.height - size * 4) + size * 2;
        const dx = Math.random() * 0.4 - 0.2;
        const dy = Math.random() * 0.4 - 0.2;
        particlesArray.push(
          new Particle(x, y, dx, dy, size, "rgba(255,255,255,0.5)")
        );
      }
    }

    function connect() {
      for (let a = 0; a < particlesArray.length; a++) {
        for (let b = a; b < particlesArray.length; b++) {
          const dx = particlesArray[a].x - particlesArray[b].x;
          const dy = particlesArray[a].y - particlesArray[b].y;
          const distSq = dx * dx + dy * dy;
          const thresh = (canvas.width / 7) * (canvas.height / 7);
          if (distSq < thresh) {
            const opacity = 1 - distSq / 20000;
            const mdx = (mouse.x ?? -999) - particlesArray[a].x;
            const mdy = (mouse.y ?? -999) - particlesArray[a].y;
            const md = Math.hypot(mdx, mdy);
            ctx.strokeStyle =
              md < mouse.radius ? "#D8315B" : `rgba(255,255,255,${opacity})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(particlesArray[a].x, particlesArray[a].y);
            ctx.lineTo(particlesArray[b].x, particlesArray[b].y);
            ctx.stroke();
          }
        }
      }
    }

    function animate() {
      requestAnimationFrame(animate);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particlesArray.forEach((p) => p.update());
      connect();
    }

    function onIntersect(entries: IntersectionObserverEntry[]) {
      entries.forEach((entry) => {
        const content = entry.target.querySelector<HTMLElement>(".content");
        if (content) {
          content.classList.toggle("visible", entry.isIntersecting);
        }
      });
    }

    return () => {
      window.removeEventListener("mousemove", onMouseover);
      window.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("resize", onResize);
      observer.disconnect();
    };
  }, []);

  const [newsItems, setNewsItems] = useState<CardViewProps[]>([]);
  const [offset, setOffset] = useState<number | null>(null);
  const [hasMore, setMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const pageSize = 6;

  const fetchPage = useCallback(async () => {
    if (loading || !hasMore) return;
    setLoading(true);
    const news_status = offset === null ? "" : String(offset);

    try {
      const response = await axios.post<CardViewProps[]>("/selectQueryNews", {
        news_status,
        news_flag: "y",
      });
      const data = response.data;
      if (data.length < pageSize) {
        setMore(false);
      }

      setNewsItems((prev) => (offset === null ? data : [...prev, ...data]));
      setOffset((prev) => (prev === null ? 0 : prev + pageSize));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [offset, loading, hasMore]);

  useEffect(() => {
    fetchPage();
  }, []);

  const digitalCount = useCountUp("2013", 2025);
  const digitalYear = useCountUp("12년", 1800);
  const receiptCount = useCountUp(receiptCounter + 600000000, 2000);
  const business = [
    {
      title: "전자영수증 플랫폼",
      description:
        "대한민국 최초의 전자영수증 발급방법 특허를 바탕으로, 금융사,\n프렌차이즈, 소상공인을 위한 혁신적인 마케팅 플랫폼을\n제공합니다. NFC 단말기, 직원 교육, 소비자 확인, 비용 부담이\n필요 없는 '4無 전자영수증'으로 진화했습니다.\n",
    },
    {
      title: "종합광고대행사",
      description:
        "친환경 브랜드와 사회적 가치를 추구하는 기업에 특화된 마케팅 파트너입니다. 데이터 기반 인사이트와 탄탄한 전략 기획을 바탕으로, 브랜드의 성장 단계에 맞춘 IMC 전략을 설계하고 실행합니다. 콘텐츠 제작부터 캠페인 운영까지 전 과정을 직접 수행하며, 지속가능성과 진정성을 중심으로 한 그린 브랜딩·크리에이티브 캠페인을 제안합니다.",
    },
    {
      title: "탄소중립 이니셔티브",
      description:
        "일상 소비를 통한 탄소 감축 방안을 세계 최초로 제안하며, 글로벌 이니셔티브를 선도하고 있습니다. 더리얼의 전자영수증은 생활소비 기반의 탄소중립 실현에 기여한 세계최초의 모범 사례입니다.",
    },
  ];

  const { hash } = useLocation();
  const animateVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 100, damping: 10 },
    },
  };

  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [hash]);

  return (
    <>
      <main>
        <section
          id="scene1"
          className="scene relative overflow-hidden bg-black"
        >
          <canvas
            id="particle-canvas"
            className="absolute inset-0 w-full h-full -z-10 bg-black"
            ref={canvasRef}
          />
          <div className="content font-sans">
            <h1 className="text-4xl laptop:text-9xl font-black text-white tracking-tighter">
              더리얼
              <span className="text-white">과 함께</span>
              <span className="highlight-red text-white"> 혁신</span>
            </h1>
            <p className="text-xl text-white pl-3 pr-3 laptop:text-2xl mt-4 font-light">
              더리얼은 소비의 패러다임을 바꾸고, 지속 가능한 내일을 만들어가는
              기술 선도 기업입니다.
            </p>
          </div>
        </section>
        <section
          id="status"
          className="p-[40px] laptop:p-20 bg-[#f8f9fa] text-black font-sans"
        >
          <div className="container max-w-[1200px] m-auto p-[1rem] block">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.0, ease: "easeIn" }}
              className="brand-banner-container-image-motion"
            >
              <div className=" text-center grid grid-cols-[repeat(auto-fit,_minmax(200px,_1fr))] gap-4">
                <div className="">
                  <h3
                    ref={digitalCount.ref}
                    className="font-extrabold text-[40px] mb-2 text-black"
                  >
                    {digitalCount.count}
                  </h3>
                  <p className="block text-black text-[1.4]">
                    대한민국 최초 전자영수증 특허등록
                  </p>
                </div>
                <div>
                  <h3
                    ref={digitalYear.ref}
                    className="font-extrabold text-[40px] mb-2 text-black"
                  >
                    {digitalYear.count}
                  </h3>
                  <p className="block text-black text-[1.4]">
                    축척된 기술과 경험
                  </p>
                </div>
                <div>
                  <h3 className="font-extrabold text-[40px] mb-2 text-black">
                    국내유일
                  </h3>
                  <p className="block text-black text-[1.4]">
                    탄소중립 글로벌 이니셔티브 플랫폼
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          <FadeInOnScroll>
            <div
              id="receipt_count"
              className="container max-w-[1200px] m-auto pt-10 font-sans"
            >
              <h3 className="font-extrabold text-2xl laptop:text-2xl mb-6 text-gray-700 text-center">
                현재 누적 전자영수증 발급 수
              </h3>
              <p
                ref={receiptCount.ref}
                className="text-[40px] font-extrabold text-black text-center pb-3"
              >
                {Number(receiptCount.count).toLocaleString()}
              </p>
                <p className="block text-black text-[1.4] text-center">
                {new Date().getMonth() + 1}월 누적 전자영수증 발급 수
                </p>

            </div>
          </FadeInOnScroll>
        </section>

        <section id="about" className="p-[40px] laptop:p-20 bg-black block">
          <FadeInOnScroll>
            <div className="container max-w-[1200px] mx-auto p-4 block">
              <div className="grid  grid-cols-1  laptop:grid-cols-2 gap-8 items-center">
                <div className="font-sans">
                  <h2 className="text-white font-extrabold text-2xl mb-6 laptop:text-3xl">
                    (주)더리얼은
                    <br />
                    대한민국 최초의
                    <br />
                    전자영수증 플랫폼
                    <br />
                    기업입니다
                  </h2>
                  <p className="text-[#666] mb-6 text-[1.7] block">
                    2013년 대한민국 최초로 전자영수증 발급방법 특허를 등록한
                    이후, 다년간의 지식, 기술 경험을 바탕으로 안정적이고
                    확장용이한 솔루션을 제공하고 있습니다.
                  </p>
                  <ul className="block list-none ">
                    <li className="relative pl-6 font-medium text-[14px] text-white p-2 before:content-['✓'] before:absolute before:left-0 before:font-bold before:text-white">
                      15년간 누적된 실전 경험
                    </li>
                    <li className="relative pl-6 font-medium text-[14px] text-white p-2 before:content-['✓'] before:absolute before:left-0 before:font-bold before:text-white">
                      광범위한 POS/VAN 네트워크
                    </li>
                    <li className="relative pl-6 font-medium text-[14px] text-white p-2 before:content-['✓'] before:absolute before:left-0 before:font-bold before:text-white">
                      친환경 마케팅 전문성
                    </li>
                    <li className="relative pl-6 font-medium text-[14px] text-white p-2 before:content-['✓'] before:absolute before:left-0 before:font-bold before:text-white">
                      데이터 기반 성과 중심 마케팅
                    </li>
                  </ul>
                </div>
                <motion.div
                  style={{
                    backgroundImage:
                      "url('/assets/images/main_relative_img.png')",
                  }}
                  variants={animateVariants}
                  className="flex items-center justify-center bg-cover bg-center rounded-[20px] h-[300px] laptop:h-full"
                  whileHover={{ scale: 1.05 }}
                >
                </motion.div>
              </div>
            </div>
          </FadeInOnScroll>
        </section>
        <section
          id="services"
          className="p-[40px] laptop:p-20 bg-[#f8f9fa] text-black font-sans"
        >
          <FadeInOnScroll>
            <div className="container max-w-[1200px] mx-auto laptop:px-4 font-sans text-center">
              <h2 className="text-[40px] mb-[48px] text-black font-extrabold">
                사업분야
              </h2>
              <div className="grid gap-8 grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-3 justify-items-center">
                {business.map((item, index) => (
                  <motion.div
                    key={index}
                    className="bg-white rounded-xl shadow-lg p-8 flex flex-col items-start gap-5"
                    variants={animateVariants}
                    whileHover={{ scale: 1.05 }}
                  >
                    <div>
                      <h3 className="text-2xl font-bold mb-4 text-left">
                        {item.title}
                      </h3>
                      <p className="text-[#666] leading-[1.8] text-left">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </FadeInOnScroll>
        </section>
        <section
          id="notice"
          className="p-[40px] laptop:p-20 bg-white font-sans"
        >
          <FadeInOnScroll>
            <div className="container mx-w-[1200px] mx-auto p-8 block">
              <h2 className="text-center text-[40px] font-extrabold mb-[48px] text-black">
                보도자료
              </h2>
              <div
                onMouseDown={(e) => e.preventDefault()}
                onTouchMove={(e) => e.preventDefault()}
                className="block"
              >
                <NewsCardListView
                  items={newsItems}
                  onLoadMore={fetchPage}
                  hasMore={hasMore}
                  loading={loading}
                />
              </div>
            </div>
          </FadeInOnScroll>
        </section>

        <main>
          <FooterView />
        </main>
      </main>
    </>
  );
};

export default MainPage;
